import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Http from "../../Common/Http";
import IsLogin from "../../Common/IsLogin";
import { LoaderComponent } from "../../Common/LoaderComponent";

const ProductLedger = () => {
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [ledgerData, setLedgerData] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [purchaseData, setPurchaseData] = useState([]);
  const [returnData, setReturnData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [returnLoading, setReturnLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);

  // Fetch all products for selection
  const fetchProducts = () => {
    setLoading(true);
    Http.get("/product-wise-stock")
      .then((res) => {
        if (res.data.status === "success") {
          setProducts(res.data.item);
          setFilteredProducts(res.data.item);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching products:", err);
        setLoading(false);
      });
  };

  // Fetch ledger data for selected product
  const fetchLedgerData = (productId) => {
    setLedgerLoading(true);
    setPurchaseLoading(true);
    setReturnLoading(true);
    
    Http.get(`/product-ledger/${productId}`)
      .then((res) => {
        if (res.data.status === "success") {
          const transactions = res.data.transactions || [];
          setLedgerData(transactions);
          setSummaryData(res.data.summary || null);
          
          // Separate purchase and return data
          const purchases = transactions.filter(t => 
            t.transaction_type === 'GRN' && t.movement_type === 'IN'
          );
          const returns = transactions.filter(t => 
            t.transaction_type === 'GRN_RETURN' && t.movement_type === 'OUT'
          );
          
          setPurchaseData(purchases);
          setReturnData(returns);
        } else {
          setLedgerData([]);
          setSummaryData(null);
          setPurchaseData([]);
          setReturnData([]);
        }
        setLedgerLoading(false);
        setPurchaseLoading(false);
        setReturnLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching ledger data:", err);
        setLedgerData([]);
        setSummaryData(null);
        setPurchaseData([]);
        setReturnData([]);
        setLedgerLoading(false);
        setPurchaseLoading(false);
        setReturnLoading(false);
      });
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // Handle URL parameters for product selection
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const productId = searchParams.get('productId');
    const productCode = searchParams.get('productCode');
    const productName = searchParams.get('productName');

    if (productId && products.length > 0) {
      // Try to find the product by ID first
      let targetProduct = products.find(p => p.id.toString() === productId);
      
      // If not found by ID, try to find by code
      if (!targetProduct && productCode) {
        targetProduct = products.find(p => p.code === productCode);
      }
      
      // If not found by code, try to find by name
      if (!targetProduct && productName) {
        targetProduct = products.find(p => p.name === decodeURIComponent(productName));
      }

      if (targetProduct) {
        setSelectedProduct(targetProduct);
        fetchLedgerData(targetProduct.id);
        // Set search term to highlight the selected product
        setSearchTerm('');
      }
    }
  }, [products, location.search]);

  // Handle product search
  useEffect(() => {
    if (searchTerm === "") {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.category_name && product.category_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredProducts(filtered);
    }
  }, [searchTerm, products]);

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    fetchLedgerData(product.id);
  };

  const getTransactionBadgeClass = (type) => {
    switch (type) {
      case 'GRN':
        return 'badge badge-success';
      case 'Issue':
        return 'badge badge-danger';
      case 'Return':
        return 'badge badge-warning';
      default:
        return 'badge badge-info';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  return (
    <div>
      <IsLogin />
      {loading && <LoaderComponent />}
      
      <div className="am-mainpanel" style={{ marginTop: "50px" }}>
        <div className="am-pagebody">
          <div className="card pd-20 pd-sm-40">
            <h3 className="card-body-title mg-sm-b-30 text-center">
              <i className="fa fa-book mr-2"></i>
              Product Stock Ledger
            </h3>
            <hr />
            {selectedProduct && summaryData && (
              <div className="row mt-4">
                <div className="col-md-3">
                  <div className="card bg-success text-white">
                    <div className="card-body text-center">
                      <h4>{summaryData.total_in || 0}</h4>
                      <p className="mb-0">Total In</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card bg-danger text-white">
                    <div className="card-body text-center">
                      <h4>{summaryData.total_out || 0}</h4>
                      <p className="mb-0">Total Out</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card bg-info text-white">
                    <div className="card-body text-center">
                      <h4>{summaryData.total_transactions || 0}</h4>
                      <p className="mb-0">Total Transactions</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card bg-primary text-white">
                    <div className="card-body text-center">
                      <h4>{summaryData.current_balance || 0}</h4>
                      <p className="mb-0">Current Balance</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="row">
              {/* Product Selection Panel */}
              <div className="col-lg-4 col-md-5">
                <div className="card">
                  <div className="card-header bg-primary text-white">
                    <h6 className="mb-0">
                      <i className="fa fa-search mr-2"></i>
                      Select Product
                    </h6>
                  </div>
                  <div className="card-body">
                    {/* Search Input */}
                    <div className="form-group">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search products..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>

                    {/* Product List */}
                    <div className="product-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {filteredProducts.map((product) => (
                        <div
                          key={product.id}
                          className={`product-item p-3 border rounded mb-2 cursor-pointer ${
                            selectedProduct?.id === product.id ? 'bg-light border-primary' : 'bg-white'
                          }`}
                          onClick={() => handleProductSelect(product)}
                          style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                          onMouseEnter={(e) => {
                            if (selectedProduct?.id !== product.id) {
                              e.target.style.backgroundColor = '#f8f9fa';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (selectedProduct?.id !== product.id) {
                              e.target.style.backgroundColor = 'white';
                            }
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <h6 className="mb-1 text-primary">{product.code}</h6>
                              <p className="mb-1 font-weight-bold">{product.name}</p>
                              <small className="text-muted">
                                {product.category_name} | {product.unit_name}
                              </small>
                            </div>
                            <div className="text-right">
                              <span className={`badge ${
                                parseFloat(product.stock_qty) <= 5 ? 'badge-danger' :
                                parseFloat(product.stock_qty) <= 10 ? 'badge-warning' : 'badge-success'
                              }`}>
                                {formatCurrency(product.stock_qty)}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Ledger Panel */}
              <div className="col-lg-8 col-md-7">
                {selectedProduct ? (
                  <div className="card">
                    <div className="card-header bg-info text-white">
                      <div className="row align-items-center">
                        <div className="col">
                          <h6 className="mb-0">
                            <i className="fa fa-list-alt mr-2"></i>
                            Stock Ledger - {selectedProduct.name}
                          </h6>
                        </div>
                        <div className="col-auto">
                          <span className="badge badge-light">
                            Current Stock: {formatCurrency(selectedProduct.stock_qty)} {selectedProduct.unit_name}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="card-body p-0">
                      {ledgerLoading ? (
                        <div className="text-center p-4">
                          <div className="spinner-border text-primary" role="status">
                            <span className="sr-only">Loading...</span>
                          </div>
                          <p className="mt-2">Loading ledger data...</p>
                        </div>
                      ) : (
                        <div className="table-responsive">
                          <table className="table table-striped table-hover mb-0">
                            <thead className="thead-dark">
                              <tr>
                                <th>Date</th>
                                <th>Type</th>
                                <th>Reference</th>
                                <th>Description</th>
                                <th className="text-right">In Qty</th>
                                <th className="text-right">Out Qty</th>
                                <th className="text-right">Balance</th>
                              </tr>
                            </thead>
                            <tbody>
                              {ledgerData.length > 0 ? (
                                ledgerData.map((transaction) => (
                                  <tr key={transaction.id}>
                                    <td>
                                      <small className="text-muted">
                                        {new Date(transaction.transaction_date).toLocaleDateString()}
                                      </small>
                                    </td>
                                    <td>
                                      <span className={getTransactionBadgeClass(transaction.transaction_type)}>
                                        {transaction.transaction_type}
                                      </span>
                                    </td>
                                    <td>
                                      <small className="font-weight-bold">
                                        {transaction.reference_no}
                                      </small>
                                    </td>
                                    <td>
                                      <small>{transaction.description}</small>
                                    </td>
                                    <td className="text-right">
                                      {transaction.movement_type === 'IN' && transaction.quantity > 0 && (
                                        <span className="text-success font-weight-bold">
                                          +{formatCurrency(Math.abs(transaction.quantity))}
                                        </span>
                                      )}
                                    </td>
                                    <td className="text-right">
                                      {transaction.movement_type === 'OUT' && transaction.quantity < 0 && (
                                        <span className="text-danger font-weight-bold">
                                          -{formatCurrency(Math.abs(transaction.quantity))}
                                        </span>
                                      )}
                                    </td>
                                    <td className="text-right">
                                      <span className="font-weight-bold text-primary">
                                        {formatCurrency(transaction.running_balance)}
                                      </span>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="7" className="text-center p-4">
                                    <i className="fa fa-info-circle text-muted mr-2"></i>
                                    No transactions found for this product
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="card">
                    <div className="card-body text-center p-5">
                      <i className="fa fa-arrow-left text-muted" style={{ fontSize: '3rem' }}></i>
                      <h5 className="mt-3 text-muted">Select a Product</h5>
                      <p className="text-muted">
                        Choose a product from the left panel to view its stock ledger
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Purchase and Return Details Section */}
            {selectedProduct && (
              <>
                {/* Purchase and Return Summary Cards */}
                <div className="row mt-4">
                  <div className="col-md-3">
                    <div className="card bg-success text-white">
                      <div className="card-body text-center">
                        <h4>{purchaseData.length}</h4>
                        <p className="mb-0">Total Purchases</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card bg-info text-white">
                      <div className="card-body text-center">
                        <h4>{formatCurrency(purchaseData.reduce((sum, p) => sum + Math.abs(p.total_amount || 0), 0))}</h4>
                        <p className="mb-0">Purchase Value</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card bg-warning text-white">
                      <div className="card-body text-center">
                        <h4>{returnData.length}</h4>
                        <p className="mb-0">Total Returns</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card bg-danger text-white">
                      <div className="card-body text-center">
                        <h4>{formatCurrency(returnData.reduce((sum, r) => sum + Math.abs(r.total_amount || 0), 0))}</h4>
                        <p className="mb-0">Return Value</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row mt-4">
                {/* Purchase Details Table */}
                <div className="col-lg-6">
                  <div className="card">
                    <div className="card-header bg-success text-white">
                      <h6 className="mb-0">
                        <i className="fa fa-shopping-cart mr-2"></i>
                        Purchase Details - {selectedProduct.name}
                      </h6>
                    </div>
                    <div className="card-body p-0">
                      {purchaseLoading ? (
                        <div className="text-center p-4">
                          <div className="spinner-border text-success" role="status">
                            <span className="sr-only">Loading...</span>
                          </div>
                          <p className="mt-2">Loading purchase data...</p>
                        </div>
                      ) : (
                        <div className="table-responsive">
                          <table className="table table-striped table-hover mb-0">
                            <thead className="thead-light">
                              <tr>
                                <th>Date</th>
                                <th>Vendor</th>
                                <th>Reference</th>
                                <th className="text-right">Quantity</th>
                                <th className="text-right">Rate</th>
                                <th className="text-right">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {purchaseData.length > 0 ? (
                                purchaseData.map((purchase) => (
                                  <tr key={purchase.id}>
                                    <td>
                                      <small className="text-muted">
                                        {new Date(purchase.transaction_date).toLocaleDateString()}
                                      </small>
                                    </td>
                                    <td>
                                      <span className="font-weight-bold text-success">
                                        {purchase.vendor_name || 'Unknown Vendor'}
                                      </span>
                                    </td>
                                    <td>
                                      <small className="font-weight-bold">
                                        {purchase.reference_no}
                                      </small>
                                    </td>
                                    <td className="text-right">
                                      <span className="text-success font-weight-bold">
                                        {formatCurrency(Math.abs(purchase.quantity))}
                                      </span>
                                    </td>
                                    <td className="text-right">
                                      <span className="text-muted">
                                        {formatCurrency(purchase.unit_price || 0)}
                                      </span>
                                    </td>
                                    <td className="text-right">
                                      <span className="font-weight-bold text-success">
                                        {formatCurrency(Math.abs(purchase.total_amount || 0))}
                                      </span>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="6" className="text-center p-4">
                                    <i className="fa fa-info-circle text-muted mr-2"></i>
                                    No purchase records found for this product
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Return Details Table */}
                <div className="col-lg-6">
                  <div className="card">
                    <div className="card-header bg-warning text-white">
                      <h6 className="mb-0">
                        <i className="fa fa-undo mr-2"></i>
                        Return Details - {selectedProduct.name}
                      </h6>
                    </div>
                    <div className="card-body p-0">
                      {returnLoading ? (
                        <div className="text-center p-4">
                          <div className="spinner-border text-warning" role="status">
                            <span className="sr-only">Loading...</span>
                          </div>
                          <p className="mt-2">Loading return data...</p>
                        </div>
                      ) : (
                        <div className="table-responsive">
                          <table className="table table-striped table-hover mb-0">
                            <thead className="thead-light">
                              <tr>
                                <th>Date</th>
                                <th>Vendor</th>
                                <th>Reference</th>
                                <th className="text-right">Quantity</th>
                                <th className="text-right">Rate</th>
                                <th className="text-right">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {returnData.length > 0 ? (
                                returnData.map((returnItem) => (
                                  <tr key={returnItem.id}>
                                    <td>
                                      <small className="text-muted">
                                        {new Date(returnItem.transaction_date).toLocaleDateString()}
                                      </small>
                                    </td>
                                    <td>
                                      <span className="font-weight-bold text-warning">
                                        {returnItem.vendor_name || 'Unknown Vendor'}
                                      </span>
                                    </td>
                                    <td>
                                      <small className="font-weight-bold">
                                        {returnItem.reference_no}
                                      </small>
                                    </td>
                                    <td className="text-right">
                                      <span className="text-danger font-weight-bold">
                                        {formatCurrency(Math.abs(returnItem.quantity))}
                                      </span>
                                    </td>
                                    <td className="text-right">
                                      <span className="text-muted">
                                        {formatCurrency(returnItem.unit_price || 0)}
                                      </span>
                                    </td>
                                    <td className="text-right">
                                      <span className="font-weight-bold text-danger">
                                        {formatCurrency(Math.abs(returnItem.total_amount || 0))}
                                      </span>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="6" className="text-center p-4">
                                    <i className="fa fa-info-circle text-muted mr-2"></i>
                                    No return records found for this product
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
               </div>
               </>
             )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductLedger;